import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { getReservation, getReservationSerialTicketsGroupedByRoute } from '../../../selectors/selectors'
import useFormatPrice from '../../../utils/formatPrice'
import Route from '../../common/Route'
import s from './SerialTicketsConfirmation.module.scss'

export default function SerialTicketsConfirmation() {
  const reservation = useSelector(getReservation)
  const groups = useSelector(getReservationSerialTicketsGroupedByRoute)

  return (
    <div className={s['serial-tickets-confirmation']}>
      {groups.map(({ from, to, tickets }) => (
        <div className={s['serial-ticket']} key={from + to}>
          <Route className={s.route} from={from} to={to} />
          <Header />
          <ConfirmationTable tickets={tickets} />
        </div>
      ))}
      <TotalPrice price={reservation.totalPrice} />
    </div>
  )
}

function Header() {
  const { t } = useTranslation()

  return (
    <div className={s.header}>
      <div className={s.label}>{t('price.description')}</div>
    </div>
  )
}

function TotalPrice({ price }) {
  const { t } = useTranslation()
  const formatPrice = useFormatPrice()

  return (
    <div className={s['total-price']}>
      <div className={s.label}>{t('price.total')}:</div>
      <div className={s.price}>{formatPrice(price)}</div>
    </div>
  )
}

TotalPrice.propTypes = {
  price: PropTypes.number.isRequired
}

function ConfirmationTable({ tickets }) {
  return (
    <>
      <TableHeader />
      <TableBody tickets={tickets} />
    </>
  )
}

const ticket = PropTypes.shape({
  code: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  from: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  totalPrice: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  unitPrice: PropTypes.number.isRequired
})

ConfirmationTable.propTypes = {
  tickets: PropTypes.arrayOf(ticket).isRequired
}

function TableHeader() {
  const { t } = useTranslation()

  return (
    <div className={s['table-header']}>
      <div className={s['title-and-amount']}>
        <div className={s.title}>{t('price.description')}</div>
        <div className={s.amount}>{t('price.amount')}</div>
      </div>
      <div className={s['price-and-total']}>
        <div className={s.price}>{t('price.price')}</div>
        <div className={s.total}>{t('price.total')}</div>
      </div>
    </div>
  )
}

TicketTitleAndAmount.propTypes = {
  count: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired
}

function TableBody({ tickets }) {
  const { t } = useTranslation()
  const formatPrice = useFormatPrice()

  return (
    <div className={s['table-body']}>
      {tickets.map(({ count, title, totalPrice, unitPrice }) => (
        <div className={s.row} key={title}>
          <TicketTitleAndAmount count={count} title={title} />
          <div className={s['price-and-total']}>
            <div className={s.price}>
              <div className={s.label}>{t('price.price')}</div>
              <div>{formatPrice(unitPrice)}</div>
            </div>
            <div className={s.total}>
              <div className={s.label}>{t('price.total')}</div>
              <div>{formatPrice(totalPrice)}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

TableBody.propTypes = {
  tickets: PropTypes.arrayOf(ticket).isRequired
}

function TicketTitleAndAmount({ count, title }) {
  return (
    <div className={s['title-and-amount']}>
      <span className={s.title} dangerouslySetInnerHTML={{ __html: title }} />
      <span className={s.times}>×</span>
      <span className={s.amount}>{count}</span>
    </div>
  )
}

TicketTitleAndAmount.propTypes = {
  count: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired
}

function PriceAndTotal({ unitPrice, totalPrice }) {
  const { t } = useTranslation()
  const formatPrice = useFormatPrice()

  return (
    <div className={s['price-and-total']}>
      <div className={s.price}>
        <div className={s.label}>{t('price.price')}</div>
        <div>{formatPrice(unitPrice)}</div>
      </div>
      <div className={s.total}>
        <div className={s.label}>{t('price.total')}</div>
        <div>{formatPrice(totalPrice)}</div>
      </div>
    </div>
  )
}

PriceAndTotal.propTypes = {
  totalPrice: PropTypes.number.isRequired,
  unitPrice: PropTypes.number.isRequired
}
